import React, { useState, useEffect, useRef } from 'react'
import { ShieldCheck, Key, CheckCircle, XCircle, Clock, Buildings, ArrowSquareOut } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useSettingsStore } from '@/stores/settingsStore'
import { authApi, getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import type { SettingsSection } from '@/types/settings'
import {
  labelClass,
  labelStyle,
  cardChromeClass,
  iconChipClass,
  iconChipAmberClass,
  iconChipNeutralClass,
} from '@/lib/styleKit'
import { revealSettingsAnchor, SETTINGS_PANEL_MOUNT_MS } from '@/lib/settingsAnchors'
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard'
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore'
import { usePermission } from '@/hooks/usePermission'

const SecuritySection: React.FC = () => {
  // Owner/Admin only (security.manage) -- a Manager/Rep/Analyst on an
  // eligible domain must not see the SSO setup card at all; clicking
  // "Create organization & set up SSO" would just 403 "Not permitted".
  const { allowed: canManageSecurity } = usePermission('security', 'manage')
  const { config, changePassword, isSaving } = useSettingsStore()
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [user, setUser] = useState<any>(null)
  const [sso, setSso] = useState<{ enabled: boolean; eligible?: boolean; configured?: boolean; domain?: string } | null>(null)
  const [ssoBusy, setSsoBusy] = useState(false)

  useEffect(() => {
    authApi.getMe().then(res => setUser(res.data)).catch(() => {})
    authApi.ssoStatus().then(res => setSso(res.data)).catch(() => setSso(null))
  }, [])

  const handleSsoSetup = async () => {
    setSsoBusy(true)
    try {
      const { data } = await authApi.ssoSetup()
      // WorkOS Admin Portal: org admin configures the IdP + verifies the domain.
      window.location.assign(data.portal_url)
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not start SSO setup'))
      setSsoBusy(false)
    }
  }

  // SSO/social accounts have no password on this app — their credentials live
  // with the identity provider. Only treat the account as passwordless once
  // /me has confirmed it, so password users never see the fields flicker away.
  const isSsoAccount = user?.can_change_password === false

  // The password fields render optimistically during that brief window before
  // /me resolves, so a value can land in one (e.g. a browser password manager
  // autofilling "Current Password") before we know the account is SSO. Once
  // it's confirmed, discard any such value instead of leaving it stuck in
  // state with no UI left to clear it -- otherwise the unsaved-changes guard
  // reports dirty forever, on every later visit, for something the user never
  // touched.
  useEffect(() => {
    if (isSsoAccount) {
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
    }
  }, [isSsoAccount])

  // Live validation for the new/confirm password pair.
  const tooShort = newPw.length > 0 && newPw.length < 6
  const mismatch = confirmPw.length > 0 && confirmPw !== newPw
  const matched = newPw.length >= 6 && confirmPw.length > 0 && confirmPw === newPw
  const canSubmit = !!currentPw && newPw.length >= 6 && newPw === confirmPw

  // Gated on `!isSsoAccount` too (not just the reset above) so the same render
  // that flips `isSsoAccount` true never reports a stale dirty value before
  // the reset effect has had a chance to run.
  const isDirty = !isSsoAccount && !!(currentPw || newPw || confirmPw)
  useUnsavedChangesGuard(isDirty)

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) {
      toast.error('Passwords do not match')
      return
    }
    if (newPw.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    try {
      await changePassword(currentPw, newPw)
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
    } catch {
      // Error already shown by store
    }
  }

  // `anchor` is the deep-link slug in the destination panel (see SettingsNav's
  // keyword table). SMTP has none: its credentials live in the sender-email
  // modals, so there is nothing in the Email Accounts panel to scroll to.
  const keyStatus: { label: string; set?: boolean; section: SettingsSection; anchor?: string }[] = [
    { label: 'OpenAI',    set: config?.openai_api_key_set,         section: 'ai-models',      anchor: 'ai-models-api-key' },
    { label: 'Anthropic', set: config?.anthropic_api_key_set,      section: 'ai-models',      anchor: 'ai-models-api-key' },
    { label: 'Apollo.io', set: config?.apollo_api_key_set,         section: 'integrations',   anchor: 'integrations-apollo' },
    { label: 'Hunter.io', set: config?.hunter_api_key_set,         section: 'integrations',   anchor: 'integrations-hunter' },
    { label: 'LinkedIn',  set: config?.linkedin_client_secret_set, section: 'integrations',   anchor: 'integrations-linkedin' },
    { label: 'SMTP',      set: config?.smtp_password_set,          section: 'email-accounts' },
  ]

  const { setActiveSection } = useSettingsStore()

  /**
   * Jump to another section's field. `SettingsPage` owns the nav's deep-link
   * scroll, but it hands `handleNav` to `SettingsNav` alone — a mounted panel
   * cannot reach it, and the store carries no anchor. So the section switch goes
   * through the same store action the nav uses, and the anchor is resolved once
   * the next panel has mounted, by the same `lib/settingsAnchors` helper the page
   * uses. Nothing about the resolution is restated here.
   */
  // Tracked so leaving Settings entirely before the panel mounts cannot scroll
  // whatever page the user landed on back to the top.
  const jumpTimer = useRef<number | null>(null)
  useEffect(() => () => { if (jumpTimer.current !== null) window.clearTimeout(jumpTimer.current) }, [])

  const requestNavigation = useUnsavedChangesStore((s) => s.requestNavigation)

  const jumpTo = (section: SettingsSection, anchor?: string) => {
    requestNavigation(() => {
      setActiveSection(section)
      if (jumpTimer.current !== null) window.clearTimeout(jumpTimer.current)
      jumpTimer.current = window.setTimeout(() => {
        jumpTimer.current = null
        revealSettingsAnchor(anchor)
      }, SETTINGS_PANEL_MOUNT_MS)
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Security
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Manage your password and review security settings.
        </p>
      </div>

      {/* Change Password */}
      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipClass}>
              <ShieldCheck className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            </div>
            <div data-settings-anchor="security-change-password">
              <CardTitle>Change Password</CardTitle>
              <CardDescription>
                {isSsoAccount ? 'You sign in through your identity provider' : 'Update your account password'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {isSsoAccount ? (
          <CardContent>
            <div className="flex items-start gap-2.5 px-3 py-3 rounded-md bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              <div>
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                  SSO accounts need no password configuration!
                </p>
                <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>
                  You sign in with Google, Microsoft, or your organization&rsquo;s SSO, so this
                  account has no password here — it&rsquo;s managed by your identity provider.
                </p>
              </div>
            </div>
          </CardContent>
        ) : (
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="security-current-password" className={labelClass} style={labelStyle}>Current Password</label>
            {/* Not autoComplete="current-password": that hint lets the browser
                silently pre-populate whatever credential it has saved for this
                site (testers saw pasted API tokens surface here and read them
                as "my password, encrypted" — AB#22118). Same suppression kit
                as MaskedField; the field must always start empty. */}
            <Input
              id="security-current-password"
              type="password"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label htmlFor="security-new-password" className={labelClass} style={labelStyle}>New Password</label>
            <Input
              id="security-new-password"
              type="password"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              placeholder="Enter new password"
              error={tooShort ? 'Must be at least 6 characters.' : undefined}
            />
          </div>
          <div>
            <label htmlFor="security-confirm-password" className={labelClass} style={labelStyle}>Confirm New Password</label>
            <Input
              id="security-confirm-password"
              type="password"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder="Confirm new password"
              error={mismatch ? 'Passwords do not match.' : undefined}
            />
            {matched && (
              <p className="mt-1.5 flex items-center gap-1 text-emerald-600 dark:text-emerald-400" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                <CheckCircle className="w-3.5 h-3.5" weight="fill" /> Passwords match.
              </p>
            )}
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleChangePassword}
            loading={isSaving}
            disabled={!canSubmit}
          >
            Change Password
          </Button>
        </CardContent>
        )}
      </Card>

      {/* Session Info */}
      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipNeutralClass}>
              <Clock className="w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" />
            </div>
            <div data-settings-anchor="security-sessions">
              <CardTitle>Session Information</CardTitle>
              <CardDescription>Your account status and activity</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={labelClass} style={labelStyle} data-settings-anchor="security-account-status">Account Status</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {user?.is_active && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                    <CheckCircle className="w-3 h-3" weight="fill" /> Active
                  </span>
                )}
                {user?.is_verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                    <CheckCircle className="w-3 h-3" weight="fill" /> Verified
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className={labelClass} style={labelStyle}>Member Since</p>
              <p className="text-foreground dark:text-[#f7f8f8] mt-1.5 tabular-nums" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enterprise SSO — only shown when WorkOS auth is on and the user has a
          company (non-personal) email domain. Lets an org admin self-serve their
          IdP setup + domain verification via the WorkOS Admin Portal. */}
      {sso?.enabled && sso?.eligible && canManageSecurity && (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <Buildings className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <div>
                <CardTitle>Single Sign-On (SSO)</CardTitle>
                <CardDescription>
                  Create your organization so your team can sign in with your company
                  Outlook or Google Workspace
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <span className={labelClass} style={{ ...labelStyle, marginBottom: 0 }}>Status</span>
              {sso.configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                  <CheckCircle className="w-3 h-3" weight="fill" /> Active &amp; verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                  <Clock className="w-3 h-3" weight="fill" /> Not configured
                </span>
              )}
            </div>
            <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
              The company Microsoft / Google buttons on the sign-in page only work for
              <em> personal</em> accounts. To let everyone use their
              <span className="text-foreground dark:text-[#f7f8f8] font-medium"> @{sso.domain}</span> company
              email, you create an organization here. The secure setup portal lets you connect
              your identity provider (Microsoft Entra, Okta, Google Workspace, or any SAML/OIDC)
              and verify ownership of <span className="text-foreground dark:text-[#f7f8f8] font-medium">{sso.domain}</span> via DNS.
              Once verified, your team signs in via “Use your company email (SSO)”.
            </p>
            <Button variant="default" size="sm" onClick={handleSsoSetup} loading={ssoBusy}>
              <ArrowSquareOut className="w-4 h-4 mr-1.5" weight="bold" />
              {sso.configured ? 'Manage organization SSO' : 'Create organization & set up SSO'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* API Keys Overview */}
      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipAmberClass}>
              <Key className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
            </div>
            <div>
              <CardTitle>API Keys Overview</CardTitle>
              <CardDescription>Quick view of configured credentials</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2" data-settings-anchor="security-api-keys">
            {keyStatus.map(k => (
              <button
                key={k.label}
                type="button"
                onClick={() => jumpTo(k.section, k.anchor)}
                className="flex items-center justify-between px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] hover:bg-muted/60 dark:hover:bg-white/[0.04] transition-colors duration-150"
              >
                <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{k.label}</span>
                {k.set ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" weight="fill" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground/40 dark:text-[#3e3e44]" />
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default SecuritySection
